// Base Service Class for CAPIVIO Platform
// Provides consistent patterns for all services
// Ensures uniform error handling, logging, and CRUD operations with serialization

import { db } from "./firebase";
import { doc, collection, setDoc, getDoc, updateDoc, deleteDoc, query, where, orderBy, limit, getDocs, serverTimestamp } from "firebase/firestore";
import { logger, LOG_CATEGORIES } from "../utils/logger";

export class BaseService {
    constructor(collectionName, serviceName, entityType = null) {
        this.collectionName = collectionName;
        this.serviceName = serviceName;
        this.entityType = entityType || this.inferEntityType(collectionName);
        this.collection = collection(db, collectionName);
    }

    // Infer entity type from collection name
    inferEntityType(collectionName) {
        const entityTypeMap = {
            "users": "User",
            "opportunities": "Opportunity", 
            "investments": "Investment",
            "agents": "Agent",
            "documents": "Document",
            "kyc": "KYC",
            "transactions": "Transaction",
            "activities": "Activity",
            "notifications": "Notification",
            "messages": "Message"
        };
        return entityTypeMap[collectionName] || "Unknown";
    }

    // Generate document reference
    getDocRef(id) {
        return doc(db, this.collectionName, id);
    }

    // Create document with consistent error handling, logging, and serialization
    async create(id, data, options = {}) {
        try {
            const docRef = this.getDocRef(id);
            
            // Prepare entity data with ID
            const entityData = {
                id,
                ...data
            };

            // Serialize entity if entityType is supported
            let documentData;
            if (this.entityType !== "Unknown") {
                try {
                    // Dynamic import to avoid circular dependency
                    const { serializationService } = await import("./serialization/serializationService");
                    const serializedEntity = await serializationService.serialize(
                        entityData,
                        this.entityType,
                        null, // Use default version
                        {
                            userId: options.userId || "system",
                            changeReason: options.changeReason || "create",
                            ipAddress: options.ipAddress,
                            userAgent: options.userAgent
                        }
                    );
                    
                    // Remove serialization metadata for storage
                    // eslint-disable-next-line no-unused-vars
                    const { version, serializedAt, serializedBy, changeReason, regulatoryRequirement, ...storageData } = serializedEntity;
                    documentData = {
                        ...storageData,
                        createdAt: serverTimestamp(),
                        updatedAt: serverTimestamp(),
                        ...options
                    };
                } catch (serializationError) {
                    await logger.warn(
                        LOG_CATEGORIES.SERIALIZATION,
                        `Serialization failed for ${this.entityType}, falling back to basic create`,
                        serializationError,
                        { entityType: this.entityType, entityId: id }
                    );
                    // Fallback to basic create without serialization
                    documentData = {
                        ...data,
                        createdAt: serverTimestamp(),
                        updatedAt: serverTimestamp(),
                        ...options
                    };
                }
            } else {
                // Basic create for unknown entity types
                documentData = {
                    ...data,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                    ...options
                };
            }

            await setDoc(docRef, documentData);

            await logger.info(
                LOG_CATEGORIES[this.serviceName.toUpperCase()] || LOG_CATEGORIES.SYSTEM,
                `${this.serviceName} document created`,
                { id, data: this.sanitizeData(data), entityType: this.entityType }
            );

            return {
                success: true,
                id,
                data: documentData,
                message: `${this.serviceName} created successfully`,
                serialized: this.entityType !== "Unknown"
            };
        } catch (error) {
            await logger.error(
                LOG_CATEGORIES[this.serviceName.toUpperCase()] || LOG_CATEGORIES.SYSTEM,
                `Failed to create ${this.serviceName}`,
                error,
                { id, data: this.sanitizeData(data), entityType: this.entityType }
            );
            throw new Error(`Failed to create ${this.serviceName}: ${error.message}`);
        }
    }

    // Get document by ID with consistent error handling, logging, and deserialization
    async getById(id) {
        try {
            const docRef = this.getDocRef(id);
            const docSnap = await getDoc(docRef);

            if (!docSnap.exists()) {
                await logger.warn(
                    LOG_CATEGORIES[this.serviceName.toUpperCase()] || LOG_CATEGORIES.SYSTEM,
                    `${this.serviceName} not found`,
                    { id, entityType: this.entityType }
                );
                return null;
            }

            const data = docSnap.data();
            
            // Deserialize entity if entityType is supported
            let result;
            if (this.entityType !== "Unknown") {
                try {
                    // Dynamic import to avoid circular dependency
                    const { serializationService } = await import("./serialization/serializationService");
                    
                    // Add ID to data for deserialization
                    const entityWithId = {
                        id: docSnap.id,
                        ...data
                    };
                    
                    const deserializedEntity = await serializationService.deserialize(
                        entityWithId,
                        this.entityType,
                        {
                            userId: "system",
                            purpose: "retrieval"
                        }
                    );
                    
                    result = {
                        id: docSnap.id,
                        ...deserializedEntity
                    };
                } catch (deserializationError) {
                    await logger.warn(
                        LOG_CATEGORIES.SERIALIZATION,
                        `Deserialization failed for ${this.entityType}, returning raw data`,
                        deserializationError,
                        { entityType: this.entityType, entityId: id }
                    );
                    // Fallback to raw data
                    result = {
                        id: docSnap.id,
                        ...data
                    };
                }
            } else {
                // Basic retrieval for unknown entity types
                result = {
                    id: docSnap.id,
                    ...data
                };
            }
            
            await logger.debug(
                LOG_CATEGORIES[this.serviceName.toUpperCase()] || LOG_CATEGORIES.SYSTEM,
                `${this.serviceName} retrieved`,
                { id, entityType: this.entityType }
            );

            return result;
        } catch (error) {
            await logger.error(
                LOG_CATEGORIES[this.serviceName.toUpperCase()] || LOG_CATEGORIES.SYSTEM,
                `Failed to get ${this.serviceName}`,
                error,
                { id, entityType: this.entityType }
            );
            throw new Error(`Failed to get ${this.serviceName}: ${error.message}`);
        }
    }

    // Update document with consistent error handling, logging, and serialization
    async update(id, data, options = {}) {
        try {
            const docRef = this.getDocRef(id);
            
            // Get current document for comparison
            const currentDoc = await getDoc(docRef);
            if (!currentDoc.exists()) {
                throw new Error(`${this.serviceName} not found`);
            }
            
            const currentData = currentDoc.data();
            
            // Prepare entity data with ID
            const entityData = {
                id,
                ...currentData,
                ...data // Apply updates
            };

            // Serialize entity if entityType is supported
            let updateData;
            if (this.entityType !== "Unknown") {
                try {
                    // Dynamic import to avoid circular dependency
                    const { serializationService } = await import("./serialization/serializationService");
                    const serializedEntity = await serializationService.serialize(
                        entityData,
                        this.entityType,
                        null, // Use default version
                        {
                            userId: options.userId || "system",
                            changeReason: options.changeReason || "update",
                            ipAddress: options.ipAddress,
                            userAgent: options.userAgent
                        }
                    );
                    
                    // Remove serialization metadata and current data for storage
                    // eslint-disable-next-line no-unused-vars
                    const { version, serializedAt, serializedBy, changeReason, regulatoryRequirement, ...storageData } = serializedEntity;
                    updateData = {
                        ...data, // Only include the fields being updated
                        updatedAt: serverTimestamp(),
                        ...options
                    };
                } catch (serializationError) {
                    await logger.warn(
                        LOG_CATEGORIES.SERIALIZATION,
                        `Serialization failed for ${this.entityType}, falling back to basic update`,
                        serializationError,
                        { entityType: this.entityType, entityId: id }
                    );
                    // Fallback to basic update without serialization
                    updateData = {
                        ...data,
                        updatedAt: serverTimestamp(),
                        ...options
                    };
                }
            } else {
                // Basic update for unknown entity types
                updateData = {
                    ...data,
                    updatedAt: serverTimestamp(),
                    ...options
                };
            }

            await updateDoc(docRef, updateData);

            await logger.info(
                LOG_CATEGORIES[this.serviceName.toUpperCase()] || LOG_CATEGORIES.SYSTEM,
                `${this.serviceName} updated`,
                { id, data: this.sanitizeData(data), entityType: this.entityType }
            );

            return {
                success: true,
                id,
                data: updateData,
                message: `${this.serviceName} updated successfully`,
                serialized: this.entityType !== "Unknown"
            };
        } catch (error) {
            await logger.error(
                LOG_CATEGORIES[this.serviceName.toUpperCase()] || LOG_CATEGORIES.SYSTEM,
                `Failed to update ${this.serviceName}`,
                error,
                { id, data: this.sanitizeData(data), entityType: this.entityType }
            );
            throw new Error(`Failed to update ${this.serviceName}: ${error.message}`);
        }
    }

    // Delete document with consistent error handling, logging, and serialization
    async delete(id) {
        try {
            const docRef = this.getDocRef(id);
            
            // Get current document for serialization before deletion
            const currentDoc = await getDoc(docRef);
            if (!currentDoc.exists()) {
                throw new Error(`${this.serviceName} not found`);
            }
            
            const currentData = currentDoc.data();
            
            // Serialize entity before deletion if entityType is supported
            if (this.entityType !== "Unknown") {
                try {
                    // Dynamic import to avoid circular dependency
                    const { serializationService } = await import("./serialization/serializationService");
                    
                    const entityData = {
                        id,
                        ...currentData
                    };
                    
                    await serializationService.serialize(
                        entityData,
                        this.entityType,
                        null, // Use default version
                        {
                            userId: "system",
                            changeReason: "delete",
                            isDeletion: true
                        }
                    );
                } catch (serializationError) {
                    await logger.warn(
                        LOG_CATEGORIES.SERIALIZATION,
                        `Serialization failed for ${this.entityType} before deletion`,
                        serializationError,
                        { entityType: this.entityType, entityId: id }
                    );
                    // Continue with deletion even if serialization fails
                }
            }

            await deleteDoc(docRef);

            await logger.info(
                LOG_CATEGORIES[this.serviceName.toUpperCase()] || LOG_CATEGORIES.SYSTEM,
                `${this.serviceName} deleted`,
                { id, entityType: this.entityType }
            );

            return {
                success: true,
                id,
                message: `${this.serviceName} deleted successfully`,
                serialized: this.entityType !== "Unknown"
            };
        } catch (error) {
            await logger.error(
                LOG_CATEGORIES[this.serviceName.toUpperCase()] || LOG_CATEGORIES.SYSTEM,
                `Failed to delete ${this.serviceName}`,
                error,
                { id, entityType: this.entityType }
            );
            throw new Error(`Failed to delete ${this.serviceName}: ${error.message}`);
        }
    }

    // Get all documents with optional filtering and pagination
    async getAll(filters = {}, options = {}) {
        try {
            let q = this.collection;

            // Apply filters
            Object.entries(filters).forEach(([field, value]) => {
                if (value !== undefined && value !== null) {
                    q = query(q, where(field, "==", value));
                }
            });

            // Apply ordering
            if (options.orderBy) {
                q = query(q, orderBy(options.orderBy.field, options.orderBy.direction || "desc"));
            }

            // Apply limit
            if (options.limit) {
                q = query(q, limit(options.limit));
            }

            const querySnapshot = await getDocs(q);
            const documents = [];

            querySnapshot.forEach((doc) => {
                documents.push({
                    id: doc.id,
                    ...doc.data()
                });
            });

            await logger.debug(
                LOG_CATEGORIES[this.serviceName.toUpperCase()] || LOG_CATEGORIES.SYSTEM,
                `${this.serviceName} list retrieved`,
                { count: documents.length, filters }
            );

            return documents;
        } catch (error) {
            await logger.error(
                LOG_CATEGORIES[this.serviceName.toUpperCase()] || LOG_CATEGORIES.SYSTEM,
                `Failed to get ${this.serviceName} list`,
                error,
                { filters }
            );
            throw new Error(`Failed to get ${this.serviceName} list: ${error.message}`);
        }
    }

    // Get documents by user ID
    async getByUserId(userId, filters = {}, options = {}) {
        return this.getAll({ userId, ...filters }, options);
    }

    // Check if document exists
    async exists(id) {
        try {
            const docRef = this.getDocRef(id);
            const docSnap = await getDoc(docRef);
            return docSnap.exists();
        } catch (error) {
            await logger.error(
                LOG_CATEGORIES[this.serviceName.toUpperCase()] || LOG_CATEGORIES.SYSTEM,
                `Failed to check ${this.serviceName} existence`,
                error,
                { id }
            );
            return false;
        }
    }

    // Batch operations
    async batchCreate(documents) {
        try {
            const results = [];
            
            for (const { id, data, options } of documents) {
                const result = await this.create(id, data, options);
                results.push(result);
            }

            await logger.info(
                LOG_CATEGORIES[this.serviceName.toUpperCase()] || LOG_CATEGORIES.SYSTEM,
                `${this.serviceName} batch created`,
                { count: results.length }
            );

            return results;
        } catch (error) {
            await logger.error(
                LOG_CATEGORIES[this.serviceName.toUpperCase()] || LOG_CATEGORIES.SYSTEM,
                `Failed to batch create ${this.serviceName}`,
                error
            );
            throw new Error(`Failed to batch create ${this.serviceName}: ${error.message}`);
        }
    }

    async batchUpdate(updates) {
        try {
            const results = [];
            
            for (const { id, data, options } of updates) {
                const result = await this.update(id, data, options);
                results.push(result);
            }

            await logger.info(
                LOG_CATEGORIES[this.serviceName.toUpperCase()] || LOG_CATEGORIES.SYSTEM,
                `${this.serviceName} batch updated`,
                { count: results.length }
            );

            return results;
        } catch (error) {
            await logger.error(
                LOG_CATEGORIES[this.serviceName.toUpperCase()] || LOG_CATEGORIES.SYSTEM,
                `Failed to batch update ${this.serviceName}`,
                error
            );
            throw new Error(`Failed to batch update ${this.serviceName}: ${error.message}`);
        }
    }

    // Sanitize sensitive data for logging
    sanitizeData(data) {
        if (!data) return data;
        
        const sensitiveFields = [
            "password", "token", "secret", "key", "dni", "passportNumber",
            "creditCard", "bankAccount", "ssn", "personalId", "privateKey"
        ];
        
        const sanitized = { ...data };
        sensitiveFields.forEach(field => {
            if (sanitized[field]) {
                sanitized[field] = "[REDACTED]";
            }
        });
        
        return sanitized;
    }

    // Validate required fields
    validateRequiredFields(data, requiredFields) {
        const missingFields = requiredFields.filter(field => !data[field]);
        
        if (missingFields.length > 0) {
            throw new Error(`Missing required fields: ${missingFields.join(", ")}`);
        }
    }

    // Generate unique ID
    generateId() {
        return `${this.collectionName}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    // Audit logging for compliance
    async logAuditEvent(action, details = {}, userId = null) {
        await logger.audit(`${this.serviceName}_${action}`, details, userId);
    }

    // Compliance logging for regulatory requirements
    async logComplianceEvent(event, details = {}, userId = null) {
        await logger.compliance(`${this.serviceName}_${event}`, details, userId);
    }

    // Get version history for an entity
    async getVersionHistory(entityId) {
        try {
            if (this.entityType === "Unknown") {
                throw new Error("Version history not available for unknown entity types");
            }
            
            // Dynamic import to avoid circular dependency
            const { serializationService } = await import("./serialization/serializationService");
            const versionHistory = await serializationService.getVersionHistory(entityId, this.entityType);
            
            await logger.debug(
                LOG_CATEGORIES.SERIALIZATION,
                `Version history retrieved for ${this.entityType}`,
                { entityId, entityType: this.entityType, versionCount: versionHistory.length }
            );
            
            return versionHistory;
        } catch (error) {
            await logger.error(
                LOG_CATEGORIES.SERIALIZATION,
                `Failed to get version history for ${this.entityType}`,
                error,
                { entityType: this.entityType, entityId }
            );
            throw new Error(`Failed to get version history: ${error.message}`);
        }
    }

    // Compare versions of an entity
    async compareVersions(entityId, version1, version2) {
        try {
            if (this.entityType === "Unknown") {
                throw new Error("Version comparison not available for unknown entity types");
            }
            
            // Dynamic import to avoid circular dependency
            const { serializationService } = await import("./serialization/serializationService");
            const comparison = await serializationService.compareVersions(entityId, version1, version2, this.entityType);
            
            await logger.debug(
                LOG_CATEGORIES.SERIALIZATION,
                `Version comparison completed for ${this.entityType}`,
                { entityId, entityType: this.entityType, version1, version2 }
            );
            
            return comparison;
        } catch (error) {
            await logger.error(
                LOG_CATEGORIES.SERIALIZATION,
                `Failed to compare versions for ${this.entityType}`,
                error,
                { entityId, entityType: this.entityType, version1, version2 }
            );
            throw new Error(`Failed to compare versions: ${error.message}`);
        }
    }
} 